// Generic Voice Interface Component
// This component provides the UI for voice calls and can work with any provider

import {
	AlertCircle,
	CheckCircle2,
	ChevronDown,
	ChevronRight,
	Mic,
	Square,
	XCircle,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '#app/components/ui/badge.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '#app/components/ui/collapsible.tsx'
import { Progress } from '#app/components/ui/progress.tsx'
import { ScrollArea } from '#app/components/ui/scroll-area.tsx'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '#app/components/ui/tabs.tsx'

import {
	type IVoiceAgentProvider,
	type ConnectionState,
	type TranscriptEvent,
	type ProviderConfig,
} from '#app/utils/voice-providers/types.ts'

// Basic ICSR data structure
interface SimpleICSRData {
	reporterName?: string
	reporterType?: 'healthcare_professional' | 'consumer' | 'other'
	reporterContact?: string
	patientAge?: string
	patientSex?: 'male' | 'female' | 'other'
	patientWeight?: string
	eventDescription?: string
	eventOnsetDate?: string
	outcome?: 'recovered' | 'recovering' | 'not_recovered' | 'fatal' | 'unknown'
	productName?: string
	dose?: string
	indication?: string
	routeOfAdministration?: string
	concomitantMedications?: string[]
	medicalHistory?: string[]
	seriousness?: 'serious' | 'non_serious'
	causality?: 'certain' | 'probable' | 'possible' | 'unlikely' | 'unrelated'
}

function isObject(val: unknown): val is Record<string, unknown> {
	return typeof val === 'object' && val !== null
}

function isSimpleICSRData(val: unknown): val is SimpleICSRData {
	// All fields are optional; ensure it's a plain object
	return isObject(val)
}

interface Props {
	provider: IVoiceAgentProvider
	providerConfig?: ProviderConfig
	title?: string
	description?: string
}

export function VoiceInterface({
	provider,
	providerConfig,
	title = 'Voice Hotline',
	description,
}: Props) {
	const [connectionState, setConnectionState] = useState<ConnectionState>(
		provider.getConnectionState(),
	)
	const [transcript, setTranscript] = useState('')
	const [transcriptSegments, setTranscriptSegments] = useState<
		TranscriptEvent['segments']
	>([])
	const [icsrData, setIcsrData] = useState<SimpleICSRData>({})
	const [error, setError] = useState<string>()
	const [activeTab, setActiveTab] = useState('transcript')
	const [isExtracting, setIsExtracting] = useState(false)

	const transcriptRef = useRef<HTMLDivElement>(null)
	const extractionIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined)

	// Set up provider event listeners
	useEffect(() => {
		provider.setEventListeners({
			onStateChange: (state) => {
				setConnectionState(state)
			},
			onTranscript: (event) => {
				setTranscript(event.fullTranscript)
				setTranscriptSegments(event.segments)
			},
			onDataExtracted: (data) => {
				setIcsrData((prev) => ({ ...prev, ...data }))
			},
			onError: (err) => {
				setError(err.message)
				toast.error(err.message)
			},
		})

		return () => {
			provider.removeEventListeners()
		}
	}, [provider])

	// Auto-scroll transcript to bottom
	useEffect(() => {
		if (transcriptRef.current) {
			transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight
		}
	}, [transcriptSegments])

	// Extract data using LLM every 2 seconds when report tab is active
	useEffect(() => {
		if (activeTab !== 'report' || !transcript || transcript.length < 50) {
			if (extractionIntervalRef.current) {
				clearInterval(extractionIntervalRef.current)
				extractionIntervalRef.current = undefined
			}
			return
		}

		const performExtraction = async () => {
			setIsExtracting(true)
			try {
				const response = await fetch('/api/transcript-extract', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ transcript }),
				})

				if (response.ok) {
					const raw: unknown = await response.json()
					if (isObject(raw) && 'data' in raw) {
						const dataVal = raw['data']
						if (isSimpleICSRData(dataVal)) {
							setIcsrData(dataVal)
						}
					}
				}
			} catch (error) {
				console.error('Failed to extract data with LLM:', error)
				// Fall back to provider's extraction
			} finally {
				setIsExtracting(false)
			}
		}

		void performExtraction()

		extractionIntervalRef.current = setInterval(() => {
			void performExtraction()
		}, 2000)

		return () => {
			if (extractionIntervalRef.current) {
				clearInterval(extractionIntervalRef.current)
				extractionIntervalRef.current = undefined
			}
		}
	}, [transcript, activeTab])

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			provider.destroy()
			if (extractionIntervalRef.current) {
				clearInterval(extractionIntervalRef.current)
			}
		}
	}, [provider])

	const handleStartCall = async () => {
		try {
			setError(undefined)
			await provider.connect(providerConfig)
			toast.success(`Connected to ${provider.getProviderName()}`)
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Connection failed'
			setError(errorMessage)
			toast.error(errorMessage)
		}
	}

	const handleEndCall = async () => {
		await provider.disconnect()
		toast.info('Call ended')
	}

	const handleToggleCall = () => {
		if (connectionState.isConnected || connectionState.isRecording) {
			void handleEndCall()
		} else if (!connectionState.isConnecting) {
			void handleStartCall()
		}
	}

	return (
		<div className="flex min-h-screen flex-col overflow-auto bg-gradient-to-br from-slate-50 to-slate-100 lg:h-screen lg:max-h-screen lg:flex-row lg:overflow-hidden">
			{/* Left Panel - Voice Interface */}
			<div className="relative flex min-h-[50vh] flex-1 flex-col items-center justify-center p-3 py-6 lg:min-h-0 lg:p-8">
				{/* Provider Badge */}
				<div className="absolute top-4 left-4">
					<Badge variant="outline" className="text-xs">
						{provider.getProviderName()}
					</Badge>
				</div>

				{/* Title and Description */}
				{(title || description) && (
					<div className="mb-6 text-center">
						{title && <h1 className="text-2xl font-bold">{title}</h1>}
						{description && (
							<p className="text-muted-foreground mt-2">{description}</p>
						)}
					</div>
				)}

				{/* Main Voice Button */}
				<div className="flex flex-col items-center space-y-6">
					<VoiceButton
						onClick={handleToggleCall}
						isActive={
							connectionState.isConnected || connectionState.isRecording
						}
						isConnecting={connectionState.isConnecting}
						text={
							connectionState.isConnecting
								? 'Connecting...'
								: connectionState.isConnected || connectionState.isRecording
									? 'Push to end conversation'
									: 'Push to start conversation'
						}
					/>
				</div>

				{/* Error Display */}
				{error && (
					<div className="absolute right-8 bottom-8 left-8 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
						{error}
					</div>
				)}
			</div>

			{/* Right Panel - Information Tabs */}
			<div className="flex min-h-[50vh] flex-1 flex-col border-t border-neutral-200 bg-white lg:h-screen lg:border-t-0 lg:border-l">
				<Tabs
					defaultValue="transcript"
					value={activeTab}
					onValueChange={setActiveTab}
					className="flex h-full flex-col overflow-hidden"
				>
					<TabsList className="grid w-full flex-shrink-0 grid-cols-2 rounded-none border-b">
						<TabsTrigger value="transcript" className="rounded-none py-3">
							<div className="flex items-center justify-center space-x-2">
								{connectionState.isRecording && (
									<div className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
								)}
								<span>
									{connectionState.isRecording
										? 'Live Transcript'
										: 'Transcript'}
								</span>
							</div>
						</TabsTrigger>
						<TabsTrigger value="report" className="rounded-none py-3">
							Report
						</TabsTrigger>
					</TabsList>

					<TabsContent
						value="transcript"
						className="flex-1 overflow-hidden p-0"
					>
						<div className="flex h-full flex-col">
							{/* Transcript Content */}
							<div className="flex flex-1 flex-col">
								{transcript && (
									<div className="flex justify-end border-b px-6 py-3">
										<Button
											onClick={() => {
												setTranscript('')
												setTranscriptSegments([])
												setIcsrData({})
											}}
											variant="outline"
											size="sm"
										>
											Clear
										</Button>
									</div>
								)}

								<ScrollArea className="flex-1 p-6">
									<div ref={transcriptRef} className="space-y-4">
										{transcriptSegments.length > 0 ? (
											transcriptSegments.map((segment, index) => (
												<div
													key={index}
													className={`flex ${
														segment.speaker === 'agent'
															? 'justify-start'
															: 'justify-end'
													}`}
												>
													<div
														className={`max-w-[80%] rounded-lg px-4 py-2 ${
															segment.speaker === 'agent'
																? 'bg-gray-100 text-gray-900'
																: 'bg-blue-500 text-white'
														}`}
													>
														<div className="mb-1 text-xs font-semibold opacity-70">
															{segment.speaker === 'agent' ? 'Agent' : 'Caller'}
														</div>
														<div className="text-sm">{segment.text}</div>
													</div>
												</div>
											))
										) : (
											<div className="flex h-64 items-center justify-center text-sm text-gray-500 italic">
												Transcript will appear here when you start talking...
											</div>
										)}
									</div>
								</ScrollArea>
							</div>
						</div>
					</TabsContent>

					<TabsContent value="report" className="flex-1 overflow-hidden p-0">
						<SimpleReport icsrData={icsrData} isExtracting={isExtracting} />
					</TabsContent>
				</Tabs>
			</div>
		</div>
	)
}

// Voice Button Component
interface VoiceButtonProps {
	onClick: () => void
	isActive: boolean
	isConnecting: boolean
	text: string
}

function VoiceButton({
	onClick,
	isActive,
	isConnecting,
	text,
}: VoiceButtonProps) {
	const isDisabled = isConnecting

	return (
		<div className="flex flex-col items-center space-y-4">
			<Button
				onClick={isDisabled ? undefined : onClick}
				disabled={isDisabled}
				className={`group relative h-24 w-24 cursor-pointer overflow-hidden rounded-full border-4 transition-all duration-300 lg:h-32 lg:w-32 ${
					isDisabled
						? 'text-muted-foreground cursor-not-allowed border-neutral-200 bg-neutral-100'
						: isActive
							? 'text-muted-foreground border-neutral-300 bg-white hover:border-red-400 hover:bg-red-500 hover:text-white'
							: 'text-foreground border-neutral-200 bg-white/80 hover:bg-white/90'
				} backdrop-blur-md`}
			>
				{/* Background waveform */}
				<div
					className={`absolute inset-0 flex items-center justify-center ${
						isActive ? 'opacity-60' : 'opacity-25'
					}`}
					style={{ transform: 'translateX(4px)' }}
				>
					<Waveform isActive={isActive} />
				</div>

				{/* Icon */}
				<div
					className={`relative z-10 flex flex-col items-center space-y-2 transition-opacity duration-200 ${
						isActive ? 'group-hover:opacity-0' : ''
					}`}
				>
					{isConnecting ? (
						<div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-600" />
					) : isActive ? (
						<div className="h-8 w-8" />
					) : (
						<Mic className="h-8 w-8" />
					)}
				</div>

				{/* Hover overlay for active state */}
				{isActive && (
					<div className="absolute inset-0 z-20 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
						<Square className="h-8 w-8" />
					</div>
				)}
			</Button>
			<p
				className={`text-lg font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}
			>
				{text}
			</p>
		</div>
	)
}

// Waveform Animation Component
function Waveform({ isActive = false }: { isActive?: boolean }) {
	return (
		<div className="flex h-16 w-full items-center justify-center space-x-1.5">
			{[...Array(7)].map((_, i) => (
				<div
					key={i}
					className={`w-1.5 rounded-full transition-all duration-500 ease-in-out ${
						isActive ? 'bg-black/80' : 'bg-white/50'
					}`}
					style={{
						height: `${Math.random() * 36 + 12}px`,
						animation: 'waveform 1.5s ease-in-out infinite',
						animationDelay: `${i * 150}ms`,
					}}
				/>
			))}
			<style>{`
				@keyframes waveform {
					0%,
					100% {
						transform: scaleY(0.5);
					}
					50% {
						transform: scaleY(1);
					}
				}
			`}</style>
		</div>
	)
}

// Simple Report Component
interface SimpleReportProps {
	icsrData: SimpleICSRData
	isExtracting?: boolean
}

function SimpleReport({ icsrData, isExtracting = false }: SimpleReportProps) {
	// Define sections for the report
	const sections = [
		{
			id: 'reporter',
			title: 'Reporter Information',
			fields: [
				{
					id: 'reporterName',
					label: 'Reporter Name',
					value: icsrData.reporterName,
					required: true,
				},
				{
					id: 'reporterType',
					label: 'Reporter Type',
					value: icsrData.reporterType,
					required: true,
				},
				{
					id: 'reporterContact',
					label: 'Contact Information',
					value: icsrData.reporterContact,
					required: true,
				},
			],
		},
		{
			id: 'patient',
			title: 'Patient Information',
			fields: [
				{
					id: 'patientAge',
					label: 'Age',
					value: icsrData.patientAge,
					required: true,
				},
				{
					id: 'patientSex',
					label: 'Sex',
					value: icsrData.patientSex,
					required: true,
				},
				{
					id: 'patientWeight',
					label: 'Weight',
					value: icsrData.patientWeight,
					required: false,
				},
			],
		},
		{
			id: 'event',
			title: 'Adverse Event Details',
			fields: [
				{
					id: 'eventDescription',
					label: 'What Happened',
					value: icsrData.eventDescription,
					required: true,
				},
				{
					id: 'eventOnsetDate',
					label: 'When Did It Start',
					value: icsrData.eventOnsetDate,
					required: true,
				},
				{
					id: 'outcome',
					label: 'Current Status',
					value: icsrData.outcome,
					required: true,
				},
			],
		},
		{
			id: 'drug',
			title: 'Suspect Drug',
			fields: [
				{
					id: 'productName',
					label: 'Suspect Drug Name',
					value: icsrData.productName,
					required: true,
				},
				{
					id: 'dose',
					label: 'Dose & Frequency',
					value: icsrData.dose,
					required: true,
				},
				{
					id: 'indication',
					label: 'Indication',
					value: icsrData.indication,
					required: true,
				},
				{
					id: 'routeOfAdministration',
					label: 'Route of Administration',
					value: icsrData.routeOfAdministration,
					required: true,
				},
			],
		},
	]

	// Calculate field counts
	const allFields = sections.flatMap((section) => section.fields)
	const requiredFields = allFields.filter((field) => field.required)
	const optionalFields = allFields.filter((field) => !field.required)
	const filledRequiredFields = requiredFields.filter((field) => field.value)
	const filledOptionalFields = optionalFields.filter((field) => field.value)

	// Calculate progress percentage based on required fields
	const progressPercentage =
		requiredFields.length > 0
			? Math.round((filledRequiredFields.length / requiredFields.length) * 100)
			: 0

	const [openSections, setOpenSections] = useState<Record<string, boolean>>({
		reporter: true,
		patient: false,
		event: false,
		drug: false,
	})

	const toggleSection = (sectionId: string) => {
		setOpenSections((prev) => ({
			...prev,
			[sectionId]: !prev[sectionId],
		}))
	}

	const formatFieldValue = (value: string | undefined): string => {
		if (!value) return ''
		return value.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())
	}

	return (
		<div className="flex h-full flex-col overflow-hidden">
			<div className="flex-shrink-0 p-6">
				<div className="mb-3 flex items-center justify-between">
					<span className="text-foreground text-sm font-medium">
						Progress {isExtracting && '(Extracting...)'}
					</span>
					<Badge variant="outline">
						{isExtracting ? (
							<div className="flex items-center gap-1">
								<div className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />
								AI Processing
							</div>
						) : (
							`${progressPercentage}%`
						)}
					</Badge>
				</div>
				<Progress value={progressPercentage} className="h-2" />
				<div className="text-muted-foreground mt-2 flex justify-between text-xs">
					<span>
						Required Fields: {filledRequiredFields.length}/
						{requiredFields.length}
					</span>
					<span>
						Optional Fields: {filledOptionalFields.length}/
						{optionalFields.length}
					</span>
				</div>
			</div>

			<div className="flex-1 overflow-hidden">
				<ScrollArea className="h-full px-6">
					<div className="space-y-2">
						{sections.map((section) => {
							const filledFields = section.fields.filter((f) => f.value).length
							const totalFields = section.fields.length
							const requiredFields = section.fields.filter(
								(f) => f.required,
							).length
							const filledRequiredFields = section.fields.filter(
								(f) => f.required && f.value,
							).length

							return (
								<Collapsible
									key={section.id}
									open={openSections[section.id]}
									onOpenChange={() => toggleSection(section.id)}
								>
									<CollapsibleTrigger className="w-full">
										<div className="flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-neutral-50">
											<div className="flex items-center space-x-3">
												{openSections[section.id] ? (
													<ChevronDown className="text-muted-foreground h-4 w-4" />
												) : (
													<ChevronRight className="text-muted-foreground h-4 w-4" />
												)}
												<span className="text-left font-medium">
													{section.title}
												</span>
											</div>
											<div className="flex items-center space-x-2">
												<Badge variant="outline" className="text-xs">
													{filledRequiredFields}/{requiredFields} required
												</Badge>
												<Badge variant="secondary" className="text-xs">
													{filledFields}/{totalFields} total
												</Badge>
											</div>
										</div>
									</CollapsibleTrigger>

									<CollapsibleContent>
										<div className="ml-6 space-y-3 border-l-2 border-neutral-100 py-2 pl-4">
											{section.fields.map((field) => {
												// Determine the appropriate icon
												const getFieldIcon = () => {
													if (field.value) {
														// Field has a value - green checkmark
														return (
															<CheckCircle2 className="h-4 w-4 text-green-500" />
														)
													} else if (field.required) {
														// Required field without value - red X
														return <XCircle className="h-4 w-4 text-red-500" />
													} else {
														// Optional field without value - yellow warning
														return (
															<AlertCircle className="h-4 w-4 text-yellow-500" />
														)
													}
												}

												return (
													<div
														key={field.id}
														className="flex items-center justify-between py-2"
													>
														<div className="flex items-center space-x-2">
															<span className="text-muted-foreground text-sm">
																{field.label}
															</span>
															{field.required && (
																<span className="text-xs text-red-500">*</span>
															)}
														</div>
														<div className="flex items-center space-x-3">
															<div className="text-sm font-medium">
																{field.value ? (
																	<span className="text-foreground">
																		{formatFieldValue(field.value)}
																	</span>
																) : (
																	<span className="text-muted-foreground italic">
																		Not collected
																	</span>
																)}
															</div>
															{getFieldIcon()}
														</div>
													</div>
												)
											})}
										</div>
									</CollapsibleContent>
								</Collapsible>
							)
						})}
					</div>
				</ScrollArea>
			</div>
		</div>
	)
}
